import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CardMedia from '@mui/material/CardMedia';
// import html2pdf from 'html2pdf.js';




const Tools = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  //const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));
  
  // Add these three ref declarations
  const standupResultsRef = useRef(null);
  const retroResultsRef = useRef(null);
  const planningResultsRef = useRef(null);
  
  // State for Daily Standup Generator
  const [standupOpen, setStandupOpen] = useState(false);
  const [teamSize, setTeamSize] = useState(5);
  const [duration, setDuration] = useState(15); // Add duration state
  const [standupQuestions, setStandupQuestions] = useState([
    'What did you accomplish yesterday?',
    'What will you work on today?',
    'Are there any impediments in your way?',
    'How close are we to meeting our sprint goal?'
  ]);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);

  // State for Retrospective Template Builder
  const [retroOpen, setRetroOpen] = useState(false);
  const [retroType, setRetroType] = useState('sailboat');
  const [retroTemplate, setRetroTemplate] = useState(null);

  // State for Sprint Planning Calculator
  const [planningOpen, setPlanningOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState(5);
  const [availabilityPercent, setAvailabilityPercent] = useState(80);
  const [previousVelocity, setPreviousVelocity] = useState(40);
  const [sprintDuration, setSprintDuration] = useState(2);
  const [planningResults, setPlanningResults] = useState(null);

  // Daily Standup Generator functions
  const handleStandupOpen = () => setStandupOpen(true);
  const handleStandupClose = () => setStandupOpen(false);
  
  const addStandupQuestion = () => {
    setStandupQuestions([...standupQuestions, '']);
  };
  
  const removeStandupQuestion = (index) => {
    const newQuestions = [...standupQuestions];
    newQuestions.splice(index, 1);
    setStandupQuestions(newQuestions);
  };
  
  const handleQuestionChange = (index, value) => {
    const newQuestions = [...standupQuestions];
    newQuestions[index] = value;
    setStandupQuestions(newQuestions);
  };
  
  const generateStandup = () => {
    // Shuffle questions for each team member
    const result = [];
    for (let i = 1; i <= teamSize; i++) {
      const shuffled = [...standupQuestions].sort(() => 0.5 - Math.random());
      result.push({
        member: `Team Member ${i}`,    
        questions: shuffled
      });
    }
    setGeneratedQuestions(result);
  };

  // Retrospective Template Builder functions
  const handleRetroOpen = () => setRetroOpen(true);
  const handleRetroClose = () => setRetroOpen(false);
  
  const generateRetroTemplate = () => {
    let template;
    switch (retroType) {
      case 'sailboat':
        template = {
          title: 'Sailboat Retrospective',
          sections: [
            { name: 'Wind (What pushes us forward)', items: [] },
            { name: 'Anchor (What holds us back)', items: [] },
            { name: 'Rocks (Risks ahead)', items: [] },
            { name: 'Island (Our goal)', items: [] }
          ]
        };
        break;
      case 'madSadGlad':
        template = {
          title: 'Mad, Sad, Glad Retrospective',
          sections: [
            { name: 'Mad (What frustrated us)', items: [] },
            { name: 'Sad (What disappointed us)', items: [] },
            { name: 'Glad (What made us happy)', items: [] }
          ]
        };
        break;
      case 'startStopContinue':
        template = {
          title: 'Start, Stop, Continue Retrospective',
          sections: [
            { name: 'Start (What we should begin doing)', items: [] },
            { name: 'Stop (What we should stop doing)', items: [] },
            { name: 'Continue (What\'s working well)', items: [] },
          ]
        };
        break;
      default:
        template = null;
    }
    setRetroTemplate(template);
  };

  // Sprint Planning Calculator functions
  const handlePlanningOpen = () => setPlanningOpen(true);
  const handlePlanningClose = () => setPlanningOpen(false);
  
  const calculateSprintCapacity = () => {
    // Simple calculation for demonstration
    const workingDays = sprintDuration * 5; // Assuming 5 working days per week
    const hoursPerDay = 8; // Assuming 8-hour workdays
    const totalHours = workingDays * hoursPerDay * teamMembers;
    const availableHours = totalHours * (availabilityPercent / 100);
    const suggestedVelocity = previousVelocity * (availabilityPercent / 100);
    
    setPlanningResults({
      totalHours,
      availableHours: Math.round(availableHours),
      suggestedVelocity: Math.round(suggestedVelocity),
      storyPointsPerHour: previousVelocity / (totalHours * (availabilityPercent / 100))
    });
  };

  // Then update the tools array
const tools = [
  {
    title: 'Daily Standup Generator',
    description: 'Create randomized standup questions to keep your daily meetings fresh and engaging.',
    action: handleStandupOpen,
    image: '/images/DailyStandupGen.png'
  },
  {
    title: 'Retrospective Template Builder',
    description: 'Generate templates for different retrospective formats like Sailboat, Mad-Sad-Glad, and more.',
    action: handleRetroOpen,
    image: '/images/RetrospectiveTemp.png'
  },
  {
    title: 'Sprint Planning Calculator',
    description: 'Calculate team capacity and suggested velocity based on historical data and availability.',
    action: handlePlanningOpen,
    image: '/images/SprintPlanningCalcu.png'
  }
];



// PDF generation functions
const generatePDF = (contentRef, filename) => {
  if (!contentRef.current) return;
  
  // eslint-disable-next-line
  const options = {
    filename: filename,
    margin: 10,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
  };
  
  // html2pdf().set(options).from(contentRef.current).save();
};

  return (
    <>
      <Helmet>
        <title>Scrum Tools &amp; Templates | ScrumTherapist</title>
        <link rel="canonical" href="https://www.scrumtherapist.com/tools" />
        <meta name="description" content="Interactive Scrum tools: standup generator, retrospective templates, and sprint planning calculator." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://www.scrumtherapist.com/tools#webpage",
            "url": "https://www.scrumtherapist.com/tools",
            "name": "Scrum Tools & Templates",
            "description": "Interactive Scrum tools: standup generator, retrospective templates, and sprint planning calculator.",
            "isPartOf": { "@id": "https://www.scrumtherapist.com/#website" }
          })}
        </script>
      </Helmet>
    <Container maxWidth="lg">
      <Box sx={{ my: { xs: 3, md: 4, lg: 5 } }}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          align="center"
          color="primary.dark"
          sx={{ fontSize: { xs: '2rsecondary dark5rem', lg: '3rem' } }}
        >
          Scrum Tools & Templates
        </Typography>
        <Typography 
          variant="h6" 
          align="center" 
          color="text.secondary" 
          paragraph
          sx={{ fontSize: { xs: '1rem', md: '1.1rem', lg: '1.25rem' } }}
        >
          Interactive tools to help your Scrum team succeed
        </Typography>
      </Box>

<Grid container spacing={{ xs: 2, md: 3, lg: 4 }} sx={{ mb: { xs: 4, md: 6, lg: 8 } }}>
  {tools.map((tool, index) => (
    <Grid item key={index} xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
      <Card sx={{ 
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        height: '100%'
      }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
            <Box sx={{ flexGrow: 1, pr: 2 }}>
              <Typography 
                gutterBottom 
                variant="h5" 
                component="h2"
                sx={{ fontSize: { xs: '1.25rem', md: '1.4rem', lg: '1.5rem' } }}
              >
                {tool.title}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
                {tool.description}
              </Typography>
            </Box>
            <Box sx={{ width: '30%', height: '30%', flexShrink: 0 }}>
              <CardMedia
                component="img"
                image={process.env.PUBLIC_URL + tool.image}
                alt={tool.title}
                sx={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain',
                  borderRadius: 1 
                }}
              />
            </Box>
          </Box>
        </CardContent>
        <CardActions>
          <Button 
            size={isDesktop ? "medium" : "small"} 
            onClick={tool.action}
            sx={{ fontSize: { xs: '0.85rem', md: '0.9rem', lg: '1rem' } }}
          >
            Open Tool
          </Button>
        </CardActions>
      </Card>
    </Grid>
  ))}
</Grid>
      
      {/* Daily Standup Generator Dialog */}
      <Dialog open={standupOpen} onClose={handleStandupClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Daily Standup Generator</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}>
              Configure your standup questions and team size to generate a randomized set of questions for each team member.
            </Typography>
          </Box>
          
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Team Size"
                type="number"
                value={teamSize}
                onChange={(e) => setTeamSize(Math.max(1, parseInt(e.target.value) || 1))}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Duration (Minutes)"
                type="number"
                value={duration}
                onChange={(e) => setDuration(Math.max(1, parseInt(e.target.value) || 15))}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
          
          {/* ... rest of the Daily Standup Generator Dialog ... */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Standup Questions
            <IconButton color="primary" onClick={addStandupQuestion} size="small" sx={{ ml: 1 }}>
              <AddIcon />
            </IconButton>
          </Typography>
          
          {standupQuestions.map((question, index) => (
            <Box key={index} sx={{ display: 'flex', mb: 1 }}>
              <TextField
                value={question}
                onChange={(e) => handleQuestionChange(index, e.target.value)}
                fullWidth
                margin="dense"
              />
              <IconButton color="error" onClick={() => removeStandupQuestion(index)} disabled={standupQuestions.length <= 1}>
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          
          <Button 
            variant="contained" 
            color="primary" 
            onClick={generateStandup}
            sx={{ mt: 3 }}
          >
            Generate Standup
          </Button>
          
          {generatedQuestions.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Generated Standup</Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<DownloadIcon />}
                  onClick={() => generatePDF(standupResultsRef, 'daily-standup.pdf')}
                >
                  Download PDF
                </Button>
              </Box>
              <Box ref={standupResultsRef}>
                {generatedQuestions.map((item, index) => (
                  <Card key={index} sx={{ mb: 2, p: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">{item.member}</Typography>
                    <List dense>
                      {item.questions.map((q, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={q} />
                        </ListItem>
                      ))}
                    </List>
                  </Card>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleStandupClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Retrospective Template Builder Dialog */}
      <Dialog open={retroOpen} onClose={handleRetroClose} maxWidth="md" fullWidth>
        <DialogTitle>Retrospective Template Builder</DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Select a retrospective format to generate a template for your next team retrospective.
            </Typography>
          </Box>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Retrospective Type</InputLabel>
            <Select
              value={retroType}
              onChange={(e) => setRetroType(e.target.value)}
              label="Retrospective Type"
            >
              <MenuItem value="sailboat">Sailboat</MenuItem>
              <MenuItem value="madSadGlad">Mad, Sad, Glad</MenuItem>
              <MenuItem value="startStopContinue">Start, Stop, Continue</MenuItem>
            </Select>
          </FormControl>
         
          <Button 
            variant="contained" 
            color="primary" 
            onClick={generateRetroTemplate}
            sx={{ mt: 3 }}
          >
            Generate Template
          </Button>
          
          {retroTemplate && (
            <Box sx={{ mt: 4 }} component="div">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{retroTemplate.title}</Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<DownloadIcon />}
                  onClick={() => generatePDF(retroResultsRef, 'retrospective-template.pdf')}
                >
                  Download PDF
                </Button>
              </Box>
              <Box ref={retroResultsRef}>
                <Grid container spacing={2}>
                  {retroTemplate.sections.map((section, index) => (
                    <Grid item key={index} xs={12} md={6} lg={retroType === 'sailboat' ? 3 : 4}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            {section.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Add sticky notes here during the retrospective
                          </Typography>
                        </CardContent>
                      </Card>                  
                    </Grid>                  
                  ))}
                  <Grid item xs={12} md={6}>
                     <Card>
                     <CardContent>                      
                      <TextField  
                          label="Actions To Be Taken"                    
                          type="text"    
                         value= 'actions to be taken'
                       />
                     </CardContent>
                     </Card>
                   </Grid>
              </Grid>
            </Box>
            </Box>
          )}          
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRetroClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Sprint Planning Calculator Dialog */}
      <Dialog open={planningOpen} onClose={handlePlanningClose} maxWidth="md" fullWidth>
        <DialogTitle>Sprint Planning Calculator</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Calculate your team's capacity and suggested velocity for the upcoming sprint.
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Team Members"
                type="number"
                value={teamMembers}
                onChange={(e) => setTeamMembers(Math.max(1, parseInt(e.target.value) || 1))}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Availability (%)"
                type="number"
                value={availabilityPercent}
                onChange={(e) => setAvailabilityPercent(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
                fullWidth
                margin="normal"
                helperText="Percentage of time available for sprint work"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Previous Sprint Velocity (Story Points)"
                type="number"
                value={previousVelocity}
                onChange={(e) => setPreviousVelocity(Math.max(1, parseInt(e.target.value) || 1))}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Sprint Duration (Weeks)"
                type="number"
                value={sprintDuration}
                onChange={(e) => setSprintDuration(Math.max(1, parseInt(e.target.value) || 1))}
                fullWidth
                margin="normal"
              />
            </Grid>
          </Grid>
          
          <Button 
            variant="contained" 
            color="primary" 
            onClick={calculateSprintCapacity}
            sx={{ mt: 3 }}
          >
            Calculate
          </Button>
          
          {planningResults && (
            <Box sx={{ mt: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Sprint Capacity Results</Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<DownloadIcon />}
                  onClick={() => generatePDF(planningResultsRef, 'sprint-planning.pdf')}
                >
                  Download PDF
                </Button>
              </Box>
              <Box ref={planningResultsRef}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold">Total Team Hours</Typography>
                        <Typography variant="h4">{planningResults.totalHours}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold">Available Hours for Sprint Work</Typography>
                        <Typography variant="h4">{planningResults.availableHours}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold">Suggested Velocity (Story Points)</Typography>
                        <Typography variant="h4">{planningResults.suggestedVelocity}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold">Story Points per Hour</Typography>
                        <Typography variant="h4">{planningResults.storyPointsPerHour.toFixed(2)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePlanningClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
};

export default Tools;
